import {useState, useEffect} from 'react'
import {SettingPageTitle} from '../components/header/SettingPageTitle'
import {useAuth} from '../../../modules/auth'
import db from '../../../../services/firebase'
import {doc, getDoc, serverTimestamp, setDoc} from 'firebase/firestore'
import {showErrorToast, showSuccessToast} from 'app/components/ShowToast'
import {ToastContainer} from 'react-toastify'
import {KTCard} from '_metronic/helpers'
import {ListLoading} from 'app/components/ListLoading'

const SettingSecurity = () => {
  const {currentUser} = useAuth()
  const [timeoutValue, setTimeoutValue] = useState<string>('Select the total days')
  const [isSaving, setIsSaving] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [isDataFetching, setIsDataFetching] = useState<boolean>(true)

  useEffect(() => {
    if (!currentUser?.tenantId) {
      setIsLoading(false)
      return
    }

    const fetchTimeoutSetting = async () => {
      try {
        setIsDataFetching(true)
        const tenantId = currentUser?.tenantId || ''
        const tenantRef = doc(db, 'tenants', tenantId)
        const securityRef = doc(tenantRef, 'settings', 'security')

        const securityDoc = await getDoc(securityRef)

        if (securityDoc.exists() && securityDoc.data().sessionTimeout) {
          const timeout = securityDoc.data().sessionTimeout
          setTimeoutValue(timeout)

          localStorage.setItem(`sessionTimeout_display_${tenantId}`, timeout)
          localStorage.setItem(
            `sessionTimeout_${tenantId}`,
            securityDoc.data().sessionTimeoutMs?.toString() || ''
          )
          localStorage.setItem(`sessionTimeout_${tenantId}_timestamp`, Date.now().toString())
        } else {
          setTimeoutValue('Select the total days')
        }
      } catch (error) {
        console.error('Error fetching timeout setting:', error)
        setTimeoutValue('Select the total days')
      } finally {
        setIsLoading(false)
        setTimeout(() => setIsDataFetching(false), 500)
      }
    }

    fetchTimeoutSetting()
  }, [currentUser?.tenantId])

  const fetchTimeoutSetting = async () => {
    try {
      const tenantId = currentUser?.tenantId || ''
      const tenantRef = doc(db, 'tenants', tenantId)
      const securityRef = doc(tenantRef, 'settings', 'security')

      const securityDoc = await getDoc(securityRef)

      if (securityDoc.exists() && securityDoc.data().sessionTimeout) {
        const timeout = securityDoc.data().sessionTimeout
        setTimeoutValue(timeout)
        const cachedTimeoutKey = `sessionTimeout_${tenantId}`
        localStorage.setItem(
          cachedTimeoutKey,
          securityDoc.data().sessionTimeoutMs?.toString() || ''
        )
        localStorage.setItem(`${cachedTimeoutKey}_timestamp`, Date.now().toString())
        localStorage.setItem(`sessionTimeout_display_${tenantId}`, timeout)
      }
    } catch (error) {
      console.error('Error fetching timeout setting:', error)
    }
  }

  const saveTimeoutSetting = async (newValue: string) => {
    if (!currentUser?.tenantId || newValue === 'Select the total days') return
    if (isSaving) return
    setIsSaving(true)
    try {
      const tenantId = currentUser.tenantId
      const tenantRef = doc(db, 'tenants', tenantId)
      const securityRef = doc(tenantRef, 'settings', 'security')
      let timeoutInMs = 0
      switch (newValue) {
        case '30 days':
          timeoutInMs = 30 * 24 * 60 * 60 * 1000
          break
        case '14 days':
          timeoutInMs = 14 * 24 * 60 * 60 * 1000
          break
        case '7 days':
          timeoutInMs = 7 * 24 * 60 * 60 * 1000
          break
        case '3 days':
          timeoutInMs = 3 * 24 * 60 * 60 * 1000
          break
        case '24 hours':
          timeoutInMs = 24 * 60 * 60 * 1000
          break
        case '12 hours':
          timeoutInMs = 12 * 60 * 60 * 1000
          break
        case '6 hours':
          timeoutInMs = 6 * 60 * 60 * 1000
          break
        case '2 hours':
          timeoutInMs = 2 * 60 * 60 * 1000
          break
        case '1 hours':
          timeoutInMs = 1 * 60 * 60 * 1000
          break
        default:
          timeoutInMs = 0
      }
      await setDoc(
        securityRef,
        {
          sessionTimeout: newValue,
          sessionTimeoutMs: timeoutInMs,
          updatedAt: serverTimestamp(),
          updatedBy: currentUser.uid,
        },
        {merge: true}
      )
      const now = Date.now().toString()
      const cachedDisplayKey = `sessionTimeout_display_${tenantId}`
      localStorage.setItem(cachedDisplayKey, newValue)
      localStorage.setItem(`${cachedDisplayKey}_timestamp`, now)

      const cachedTimeoutKey = `sessionTimeout_${tenantId}`
      localStorage.removeItem(cachedTimeoutKey)
      localStorage.setItem(cachedTimeoutKey, timeoutInMs.toString())
      localStorage.setItem(`${cachedTimeoutKey}_timestamp`, now)

      showSuccessToast({message: 'Timeout settings updated'})
      await fetchTimeoutSetting()
      window.dispatchEvent(new Event('timeoutSettingUpdated'))
    } catch (error) {
      console.error('Error saving timeout setting:', error)
      showErrorToast({message: 'Failed to update timeout settings'})
    } finally {
      setTimeout(() => {
        setIsSaving(false)
      }, 500)
    }
  }

  return (
    <>
      <SettingPageTitle
        icon='Settings.svg'
        title='Settings'
        desc=' Manage your settings'
        // breadcrumbs={usersBreadcrumbs}
      >
        Personnel Management
      </SettingPageTitle>
      {isDataFetching ? (
        <KTCard>
          <div className='position-relative' style={{height: '500px'}}>
            <ListLoading />
          </div>
        </KTCard>
      ) : (
        <div className='card p-2'>
          <div className='card-header border-0 pt-5 mb-1'>
            <div className='row align-items-center'>
              <div className='col'>
                <span className='card-label fw-bold fs-2'>Idle session timeout</span>{' '}
                {isSaving && (
                  <span className='spinner-border spinner-border-sm ms-2' role='status'></span>
                )}
                <p className='text-gray-700 fs-'>
                  Set the session timeout duration. Team members will be automatically logged out
                  after this period of inactivity.
                </p>
              </div>
            </div>
          </div>

          <div className='row mx-5 mb-6'>
            <div className='form-group'>
              <div className='d-flex justify-content-between align-items-center mb-2'>
                <label className='fw-semibold text-normal fs-6 mb-0'>Timeout length</label>
                {/* {timeoutValue !== 'Select the total days' && (
                  <span className='badge badge-light-success'>Current: {timeoutValue}</span>
                )} */}
              </div>
              <div className='form-check form-switch form-switch-sm form-check-custom form-check-solid'>
                <select
                  name='timeout'
                  className='form-select w-100'
                  data-control='select2'
                  data-placeholder='Select length'
                  value={isLoading ? 'Select the total days' : timeoutValue}
                  disabled={
                    isLoading ||
                    isSaving ||
                    (currentUser?.role !== 'Admin' && currentUser?.role !== 'Owner')
                  }
                  onChange={(e) => {
                    const newValue = e.target.value
                    if (newValue === timeoutValue) return
                    setTimeoutValue(newValue)

                    if (newValue !== 'Select the total days') {
                      setTimeout(() => {
                        saveTimeoutSetting(newValue)
                      }, 0)
                    }
                  }}
                >
                  <option>Select the total days</option>
                  <option>30 days</option>
                  <option>14 days</option>
                  <option>7 days</option>
                  <option>3 days</option>
                  <option>24 hours</option>
                  <option>12 hours</option>
                  <option>6 hours</option>
                  <option>2 hours</option>
                  <option>1 hours</option>
                </select>
              </div>
              <div className='form-text text-gray-700 mt-2'>
                Settings are saved automatically when you select an option.
              </div>
            </div>
          </div>
        </div>
      )}
      <ToastContainer />
    </>
  )
}

export default SettingSecurity
